import React from "react";
import { useTranslation } from "react-i18next";
import TemplateTable from "@/shared/components/ui/table/table.tsx";
import { Card, CardHeader, CardTitle, CardContent } from "@/ui/components/ui/card.tsx";
import { ColumnDef } from "@tanstack/react-table";
import { MachineEvent } from "@/machines/types/event.ts";
import { formatDate, formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { useTimelineContext } from "@/machines/context/timeline-context.tsx";
import { useGetTruckEvents } from "@/machines/api/queries/events.queries.ts";
import { Button } from "@/ui/components/ui/button.tsx";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { useFilterStore } from "@/shared/stores/useFilterStore.ts";
import { QUERY_KEYS } from "@/machines/api/config.ts";

const formatOptionalNumber = (value: number | undefined) =>
  value == null
    ? undefined
    : formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface EventsTableProps {
  device_mxid?: string;
}

const EventsTable = ({ device_mxid }: EventsTableProps) => {
  const { t } = useTranslation("machines");
  const { setSelectedTime } = useTimelineContext();

  const { value } = useDateTimeRangeStore();
  const { getFilters } = useFilterStore();

  // TODO: remove filter by the key if someone change the date...

  const filters = getFilters(`${QUERY_KEYS.MACHINES}_${device_mxid}`);


  const { data, status } = useGetTruckEvents({
    device_mxid,
    start_time: value.fromDate,
    end_time: value.toDate,
    page: filters.page ?? 1,
    itemsPerPage: filters.itemsPerPage ?? 10,
  });

  const machineEventColumns: ColumnDef<MachineEvent>[] = [
    {
      header: t("eventsTable.columns.eventId"),
      accessorKey: "eventId",
    },
    {
      header: t("eventsTable.columns.layerState"),
      accessorKey: "eventLayer",
    },
    {
      header: t("eventsTable.columns.startTime"),
      accessorKey: "startTime",
      cell: ({ row }) => formatDate(new Date(row.original.startTime), "HH:mm:ss"),
    },
    {
      header: t("eventsTable.columns.endTime"),
      accessorKey: "endTime",
      cell: ({ row }) => formatDate(new Date(row.original.endTime), "HH:mm:ss"),
    },
    {
      header: t("eventsTable.columns.cycleDuration"),
      accessorKey: "cycleDuration",
      cell: ({ row }) => formatOptionalNumber(row.original.cycleDuration),
    },
    {
      header: t("eventsTable.columns.buckets"),
      accessorKey: "buckets",
      cell: ({ row }) => formatOptionalNumber(row.original.buckets),
    },
    {
      header: t("eventsTable.columns.productivity"),
      accessorKey: "productivity",
      cell: ({ row }) => formatOptionalNumber(row.original.productivity),
    },
    {
      header: t("eventsTable.columns.fragmentation"),
      accessorKey: "fragmentation",
    },
    {
      header: t("eventsTable.columns.showOnTimeline"),
      id: "showOnTimeline",
      cell: ({ row }) => {
        const handleClick = () => {
          const timestamp = new Date(row.original.startTime).getTime() + 100;
          setSelectedTime(timestamp, "external");

          const previewElement = document.getElementById("main-preview");
          if (previewElement) {
            previewElement.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        };

        return (
          <Button variant="outline" size="sm" onClick={handleClick}>
            {t("eventsTable.show")}
          </Button>
        );
      },
      enableSorting: false,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("eventsTable.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <TemplateTable
          items={data?.data}
          pagination={data?.metadata}
          columns={machineEventColumns}
          filterKey={`${QUERY_KEYS.MACHINES}_${device_mxid}`}
          isLoading={status === "pending"}
        />
      </CardContent>
    </Card>
  );
};

export default EventsTable;
